/**
 * Face Detection Loop Module
 * Extracted from AppBouncer to handle continuous face detection during liveness/positioning screens.
 */

import { MediaPipeFaceDetector, FaceLandmarks } from '../face-detection';
import { checkSceneQuality, SceneQualityResult, Landmark } from '../face-detection';

// ============================================================
// Types
// ============================================================

export interface FaceDetectionLoopConfig {
  /** MediaPipe face detector instance */
  faceDetector: MediaPipeFaceDetector;
  /** Video element to detect faces from */
  videoElement: HTMLVideoElement;
  /** Debug mode for logging */
  debug?: boolean;
  /** Oval radius multiplier for scene quality checks (1.0=strict, 1.3=relaxed for liveness) */
  ovalRadiusMultiplier?: number;
  /** Display container width in px (needed for object-cover mapping) */
  containerWidth?: number;
  /** Display container height in px (needed for object-cover mapping) */
  containerHeight?: number;
}

export interface FaceDetectionCallbacks {
  /** Called when face detection result is available */
  onFaceDetected?: (detected: boolean, quality?: SceneQualityResult) => void;
  /** Called to check if loop should continue */
  shouldContinue?: () => boolean;
  /** Called on detection error */
  onError?: (error: unknown) => void;
}

// ============================================================
// Face Detection Loop Class
// ============================================================

/**
 * Manages continuous face detection loop for liveness and face positioning screens.
 * Runs at animation frame rate and provides landmarks to liveness detector.
 */
export class FaceDetectionLoop {
  private config: FaceDetectionLoopConfig;
  private callbacks: FaceDetectionCallbacks = {};

  // State
  private animationFrameId: number | null = null;
  private lastFaceData: FaceLandmarks | null = null;
  private isRunning = false;

  constructor(config: FaceDetectionLoopConfig) {
    this.config = config;
  }

  /**
   * Set callbacks for face detection events
   */
  setCallbacks(callbacks: FaceDetectionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Start the face detection loop
   */
  start(): void {
    if (this.isRunning) {
      this.log('Loop already running, ignoring start');
      return;
    }

    this.isRunning = true;
    this.runDetectionLoop();
  }

  /**
   * Stop the face detection loop
   */
  stop(): void {
    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.log('Face detection loop stopped');
  }

  /**
   * Get the last detected landmarks in face-utils Landmark format.
   * Used by LivenessDetector for action verification.
   */
  getLastLandmarks(): Landmark[] | null {
    if (!this.lastFaceData) return null;

    // Convert MediaPipe landmarks to face-utils Landmark format
    return this.lastFaceData.landmarks.map(([x, y, z]) => ({ x, y, z: z || 0 }));
  }

  /**
   * Get the raw last face data from MediaPipe
   */
  getLastFaceData(): FaceLandmarks | null {
    return this.lastFaceData;
  }

  /**
   * Update the oval radius multiplier at runtime.
   * Use 1.0 for strict (face positioning, age) and 1.3 for relaxed (liveness).
   */
  setOvalRadiusMultiplier(multiplier: number): void {
    this.config.ovalRadiusMultiplier = multiplier;
  }

  /**
   * Check if the loop is currently running
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Dispose resources
   */
  dispose(): void {
    this.stop();
    this.lastFaceData = null;
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private runDetectionLoop(): void {
    const detect = async (): Promise<void> => {
      if (!this.isRunning) return;

      const { videoElement, faceDetector } = this.config;

      if (!videoElement || !faceDetector) {
        this.log('Missing video element or face detector');
        return;
      }

      try {
        // Detect face using MediaPipe
        const faceData = await faceDetector.detectFace(videoElement);
        this.lastFaceData = faceData;

        if (faceData) {
          // Check scene quality using face-utils
          const landmarks = this.getLastLandmarks();
          if (landmarks && landmarks.length > 0) {
            const quality = checkSceneQuality(
              videoElement,
              landmarks,
              {
                containerWidth: this.config.containerWidth,
                containerHeight: this.config.containerHeight,
              },
              this.config.ovalRadiusMultiplier ?? 1.0
            );
            this.callbacks.onFaceDetected?.(true, quality);
          } else {
            this.callbacks.onFaceDetected?.(true);
          }
        } else {
          this.callbacks.onFaceDetected?.(false);
        }
      } catch (error) {
        this.log('Face detection error:', error);
        this.callbacks.onError?.(error);
        this.callbacks.onFaceDetected?.(false);
      }

      // Continue loop if still running and callback allows
      if (this.isRunning && (this.callbacks.shouldContinue?.() ?? true)) {
        this.animationFrameId = requestAnimationFrame(detect);
      } else {
        this.isRunning = false;
        this.animationFrameId = null;
      }
    };

    detect();
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[FaceDetectionLoop]', ...args);
    }
  }
}
